import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

export default function MoveOutDialog({ open, onOpenChange, tenancy, onSaved }) {
  const [moveOutDate, setMoveOutDate] = useState("");
  const [refunded, setRefunded] = useState(false);
  const [chargeAmount, setChargeAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("Bank Transfer");
  const [txnRef, setTxnRef] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!moveOutDate) {
      toast.error("Move-out date is required");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/tenancies/${tenancy.id}/moveout`, {
        move_out_date: moveOutDate,
        deposit_refunded: refunded,
        move_out_charge_amount: chargeAmount ? parseFloat(chargeAmount) : null,
        move_out_payment_mode: paymentMode,
        move_out_transaction_ref: txnRef || null,
        remarks,
      });
      toast.success("Move-out recorded");
      setMoveOutDate(""); setRefunded(false); setRemarks(""); setChargeAmount(""); setTxnRef("");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Could not record move-out");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="record-move-out-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Record Move-Out</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-500">Closing tenancy for <span className="font-medium text-slate-800">{tenancy?.tenant_name}</span></p>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600">Move-out Date</Label>
            <Input type="date" value={moveOutDate} onChange={(e) => setMoveOutDate(e.target.value)} data-testid="moveout-date-input" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <Label className="text-sm text-slate-700">Security deposit refunded</Label>
            <Switch checked={refunded} onCheckedChange={setRefunded} data-testid="moveout-deposit-switch" />
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 space-y-3">
            <p className="text-[11px] uppercase font-mono tracking-[0.16em] text-rose-700">Move-Out Charges &amp; Transaction</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Amount (₹)</Label>
                <Input type="number" value={chargeAmount} onChange={(e) => setChargeAmount(e.target.value)} data-testid="moveout-charge-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Mode</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger data-testid="moveout-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Bank Transfer", "UPI", "Cash", "Cheque", "Card"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Txn Ref</Label>
                <Input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} data-testid="moveout-txn-input" />
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600">Closure Remarks</Label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} placeholder="Inspection notes, deductions…" data-testid="moveout-remarks-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="moveout-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F2942] gap-2" data-testid="moveout-save-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Confirm Move-Out
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
